// shared/src/prompts/q-qa-judge.ts
// Q&A Calibration (docs/qa-calibration-2026-06-16-brief-revision.md) — the LLM
// judge for QUALITATIVE Q&A claims. The HYBRID scorer (decided 2026-06-16)
// grades price/macro claims deterministically (no LLM); this module handles the
// irreducibly qualitative ones (regime breaks, "which ticker", yes/no setups)
// where there is no single number to compare.
//
// Given a Q&A question, its four perspective answers, the model's PREDICTED
// answer, and a REALIZED-CONTEXT block (what the brief's data sources actually
// showed over the horizon — price deltas, macro changes, notable news), the
// judge returns: did the predicted answer come true (outcome), which
// perspective best matched reality (winning_perspective), the realized
// direction, and a short rationale.
//
// Sibling of synthesis-quality-crux.ts (the macro crux scorer). Called by
// workers/macro-prediction-scorer's runQaCalibrationPass for due answers whose
// scoreable_claim.kind='qualitative' (or the gated backfill of legacy answers).
// Its llm_calls rows carry step_name 'q.qa-judge:<answer_id>' so the Cost tab's
// new "Q&A calibration" category (effective_stage 'K') isolates this spend.
//
// Model: deepseek-v4-flash (Flash-by-design — short scoring call; no Pro
// deviation, same as the crux scorer).

import { z } from 'zod';
import { coercedEnum, makeEnumCoercer } from '../resolvers/llm-enum-coerce.js';
import { QaClaimDirectionEnum } from '../schemas/qa-scoreable-claim.js';

export const VERSION    = '1.0.0';   // 2026-06-16 — initial release
export const STEP       = 'q.qa-judge';
export const MODEL      = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 1200)
export const ROLE       = 'scorer' as const;
export const DESC       = 'Grades a qualitative Q&A prediction against realized outcomes at horizon' as const;   // #45 registry — one-line operator description

// ── enums ───────────────────────────────────────────────────────────────────
export const coerceQaJudgeOutcome = makeEnumCoercer({
  correct: 'correct', right: 'correct', hit: 'correct', true: 'correct', yes: 'correct',
  incorrect: 'incorrect', wrong: 'incorrect', miss: 'incorrect', false: 'incorrect', no: 'incorrect',
  partial: 'partial', partially: 'partial', mixed: 'partial', 'partially correct': 'partial',
  neutral: 'neutral', unresolved: 'neutral', 'no signal': 'neutral', no_signal: 'neutral',
  inconclusive: 'neutral', na: 'neutral', 'n/a': 'neutral',
});
export const QaJudgeOutcomeEnum = coercedEnum(['correct', 'incorrect', 'partial', 'neutral'], coerceQaJudgeOutcome);

export const coerceQaWinningPerspective = makeEnumCoercer({
  consensus: 'consensus', 'consensus path': 'consensus',
  contrarian: 'contrarian', contra: 'contrarian', 'contrarian path': 'contrarian',
  trajectory: 'trajectory', path: 'trajectory', inductive: 'trajectory',
  predicted: 'predicted', prediction: 'predicted', 'predicted call': 'predicted',
  tie: 'tie', both: 'tie', all: 'tie',
  unresolved: 'unresolved', neither: 'unresolved', none: 'unresolved', 'no match': 'unresolved',
});
export const QaWinningPerspectiveEnum = coercedEnum(
  ['consensus', 'contrarian', 'trajectory', 'predicted', 'tie', 'unresolved'],
  coerceQaWinningPerspective,
);

// ── Output schema ───────────────────────────────────────────────────────────
export const OutputSchema = z.object({
  outcome:             QaJudgeOutcomeEnum,
  realized_direction:  QaClaimDirectionEnum,        // up/down/flat/na — best-effort directional read of what happened
  winning_perspective: QaWinningPerspectiveEnum,
  reasoning:           z.string().min(20).max(1500),   // 2026-08-07: 800→1500 for parity with quality.crux-scorer — same reasoning-paragraph pattern; pre-empts the occasional over-run (advisory text, fail-soft)
}).strict();

export type Output = z.infer<typeof OutputSchema>;

// ── SYSTEM prompt ───────────────────────────────────────────────────────────
export const SYSTEM = `You are the Q&A answer judge. A prior LLM answered a market question with FOUR perspectives (consensus · contrarian · trajectory · predicted) and committed to a single PREDICTED answer at a stated horizon. That horizon has now elapsed. You are given the question, the four answers, and a REALIZED CONTEXT block describing what the underlying data actually showed over the horizon (price moves, macro changes, notable news). Grade the prediction.

Output four fields:

1. OUTCOME ("correct" | "incorrect" | "partial" | "neutral"): did the PREDICTED answer come true?
   - "correct"   — the predicted state happened as described.
   - "incorrect" — the predicted state did NOT happen (or the opposite happened).
   - "partial"   — direction/thesis right but magnitude or timing materially off, or one of multiple claims hit.
   - "neutral"   — the realized context is too sparse/ambiguous to judge, OR nothing decisive happened either way.

2. REALIZED_DIRECTION ("up" | "down" | "flat" | "na"): the net directional move of the question's subject over the horizon, if it has one. "na" for non-directional questions (e.g. "which ticker is weakest").

3. WINNING_PERSPECTIVE ("consensus" | "contrarian" | "trajectory" | "predicted" | "tie" | "unresolved"): which of the four perspectives' answers best matched what actually unfolded?
   - Pick the single perspective whose answer most closely matches the realized context.
   - "tie" if two or more matched equally well; "unresolved" if none did (or the context can't discriminate).
   - This is about which FRAMING was right, judged against the realized context — not which was most confident.

4. REASONING (20-1500 chars): one paragraph citing the SPECIFIC realized facts (price level, macro print, headline) that drove the verdict. No platitudes.

OUTPUT SCHEMA (strict JSON; first char='{'; last char='}'; no commentary):
{
  "outcome":             "correct" | "incorrect" | "partial" | "neutral",
  "realized_direction":  "up" | "down" | "flat" | "na",
  "winning_perspective": "consensus" | "contrarian" | "trajectory" | "predicted" | "tie" | "unresolved",
  "reasoning":           "..."
}

Hard rules:
- Output JSON only. No markdown fences, no commentary, no leading whitespace before '{'.
- Judge ONLY against the REALIZED CONTEXT block — never against the answers' own reasoning. If realized context is empty/insufficient, outcome="neutral", winning_perspective="unresolved".
- Do not reward confidence. A HIGH-confidence wrong call is "incorrect".`;

// ── USER prompt builder ──────────────────────────────────────────────────────
export interface QaJudgeInput {
  readonly answer_id:            string;
  readonly question_text:        string;
  readonly horizon_days:         number;
  readonly subject:              string;
  readonly consensus_answer:     string;
  readonly contrarian_answer:    string;
  readonly trajectory_answer:    string | null;
  readonly predicted_answer:     string;
  readonly predicted_reasoning:  string;
  readonly predicted_confidence: 'HIGH' | 'MED' | 'LOW' | 'UNKNOWN';
  // Assembled by the scorer from actuals over the horizon — price deltas for
  // any tickers in the question/subject, macro indicator changes, notable news.
  readonly realized_context:     string;
}

export function buildUserPrompt(input: QaJudgeInput): string {
  return `Question (answered ${input.horizon_days} days ago, horizon now elapsed):
${input.question_text}

Subject: ${input.subject}

=== FOUR PERSPECTIVE ANSWERS ===
- CONSENSUS:  ${input.consensus_answer}
- CONTRARIAN: ${input.contrarian_answer}
- TRAJECTORY: ${input.trajectory_answer ?? '(none)'}
- PREDICTED (confidence ${input.predicted_confidence}): ${input.predicted_answer}
  predicted reasoning: ${input.predicted_reasoning}

=== REALIZED CONTEXT (what the data actually showed over the horizon) ===
${input.realized_context || '(no realized context available)'}

Output the JSON verdict per the schema in the system prompt.`;
}
